/**
 * THE canonical ownership predicate (Bible §4 ownership-determination rule,
 * DD-MODAL-0723; Rule 12 — one source).
 *
 * The investigation found THREE divergent hand-rolled ownership predicates
 * inside the mobile player modal alone (badge: `!lc || lc.status ===
 * 'available' || !lc.team?.team_id`; FIN tab: `hasContract`; action bar:
 * `lc.status === 'owned'`) — agreeing only when data is fresh and complete,
 * and every one of them FAILING OPEN to a confident "Free Agent" on
 * missing/malformed league_context (Rule 9 violation class). Same disease
 * as the TWP position literal, the cash writers, and the league prop shape.
 *
 * Canonical rule:
 *   OWNED       — league_context.status === 'owned' AND a team_id is present
 *   FREE_AGENT  — league_context.status === 'available'
 *                 (the canonical modal-serializer value for unowned players)
 *   UNKNOWN     — league_context absent, or status missing/unrecognized,
 *                 or status 'owned' with no team_id (contradictory shape).
 *                 Consumers MUST render an explicit unknown/error state for
 *                 UNKNOWN — never a confident ownership claim (Rule 9).
 *
 * Reads canonical paths only (league_context.status / league_context.team).
 * No flat fallbacks, no dual-shape acceptance (Rule 2).
 */

export const OWNERSHIP = Object.freeze({
  OWNED: 'owned',
  FREE_AGENT: 'free_agent',
  UNKNOWN: 'unknown',
});

/**
 * @param {object} player — canonical player object (Bible §4 shape)
 * @returns {{
 *   state: 'owned'|'free_agent'|'unknown',
 *   teamId: string|null,
 *   teamName: string|null,
 *   isUserTeam: boolean,
 * }}
 */
export const getOwnershipState = (player) => {
  const lc = player?.league_context;
  if (!lc || typeof lc !== 'object') {
    return { state: OWNERSHIP.UNKNOWN, teamId: null, teamName: null, isUserTeam: false };
  }

  const status = lc.status;
  const teamId = lc.team?.team_id || null;

  if (status === 'owned') {
    if (!teamId) {
      // Contradictory shape: claims owned but names no team. Rule 9 —
      // surface as unknown, never guess either direction.
      return { state: OWNERSHIP.UNKNOWN, teamId: null, teamName: null, isUserTeam: false };
    }
    return {
      state: OWNERSHIP.OWNED,
      teamId,
      teamName: lc.team?.team_name || null,
      isUserTeam: lc.team?.is_user_team === true,
    };
  }

  if (status === 'available') {
    return { state: OWNERSHIP.FREE_AGENT, teamId: null, teamName: null, isUserTeam: false };
  }

  // Missing or unrecognized status: fail closed to UNKNOWN (Rule 9 —
  // absent ownership data must never render a confident "Free Agent").
  return { state: OWNERSHIP.UNKNOWN, teamId: null, teamName: null, isUserTeam: false };
};

export default getOwnershipState;
